import { CREATE_SMART_DEFINITION, FETCH_SMART_DEFINITION, FETCH_SMART_DEFINITIONS, DELETE_SMART_DEFINITION_CRITERIA, RESET_DEFINITIONS_FETCH_STATUS } from 'smart-content-core/actions/ActionTypes';
import * as criteriaClient from 'smart-content-core/api/Criteria';
import * as definitionsClient from 'smart-content-core/api/Definitions';
import { RequestStatus } from 'smart-content-core/constants';
import { getDefinition } from 'smart-content-core/selectors/Definitions';
import { TRACK_EVENT, TrackEventTypes } from 'smart-content-core/constants';
import { reportErrorToRaven } from '../utils/errors';
const createDefinitionRequested = () => ({
  type: CREATE_SMART_DEFINITION,
  status: RequestStatus.PENDING
});
const createDefinitionRequestSucceeded = (data, contentType, criterionType, targetType, moduleName) => ({
  type: CREATE_SMART_DEFINITION,
  payload: data,
  status: RequestStatus.SUCCEEDED,
  meta: {
    [TRACK_EVENT]: {
      action: 'create-definition',
      usageTrackerJsName: 'smartContentDefinitionInteraction',
      meta: {
        contentType,
        smartType: criterionType,
        targetType,
        moduleName,
        name: 'create-definition',
        type: TrackEventTypes.INTERACTION
      }
    }
  }
});
const createDefinitionRequestFailed = error => ({
  type: CREATE_SMART_DEFINITION,
  payload: error,
  error: true,
  status: RequestStatus.FAILED
});
export function createDefinition({
  contentType,
  criterionType,
  targetType,
  targetId,
  smartRules,
  moduleName
}) {
  return (dispatch, getState, httpClient) => {
    const requestPayload = {
      contentType,
      targetType,
      targetId,
      moduleName,
      criteria: smartRules
    };
    dispatch(createDefinitionRequested());
    return definitionsClient.createCombined(requestPayload, httpClient).then(data => {
      dispatch(createDefinitionRequestSucceeded(data, contentType, criterionType, targetType, moduleName));
      return data;
    }).catch(error => {
      reportErrorToRaven(error, 'Error creating definition');
      dispatch(createDefinitionRequestFailed(error));
      return error;
    });
  };
}
const definitionRequested = definitionId => ({
  type: FETCH_SMART_DEFINITION,
  status: RequestStatus.PENDING,
  meta: {
    definitionId
  }
});
const definitionRequestSucceeded = (definitionId, data) => ({
  type: FETCH_SMART_DEFINITION,
  payload: data,
  status: RequestStatus.SUCCEEDED,
  meta: {
    definitionId
  }
});
const definitionRequestFailed = (definitionId, error) => ({
  type: FETCH_SMART_DEFINITION,
  payload: error,
  error: true,
  status: RequestStatus.FAILED,
  meta: {
    definitionId
  }
});
export function fetchDefinition(definitionId) {
  return (dispatch, getState, httpClient) => {
    dispatch(definitionRequested(definitionId));
    return definitionsClient.fetch(definitionId, httpClient).then(data => {
      dispatch(definitionRequestSucceeded(definitionId, data));
      return data;
    }).catch(error => {
      reportErrorToRaven(error, 'Error fetching a single definition');
      dispatch(definitionRequestFailed(definitionId, error));
    });
  };
}
const definitionsRequested = definitionIds => ({
  type: FETCH_SMART_DEFINITIONS,
  status: RequestStatus.PENDING,
  meta: {
    definitionIds
  }
});
const definitionsRequestSucceeded = (definitionIds, payload) => ({
  type: FETCH_SMART_DEFINITIONS,
  payload,
  status: RequestStatus.SUCCEEDED,
  meta: {
    definitionIds
  }
});
const definitionsRequestFailed = (definitionIds, error) => ({
  type: FETCH_SMART_DEFINITIONS,
  payload: error,
  error: true,
  status: RequestStatus.FAILED,
  meta: {
    definitionIds
  }
});
export function fetchDefinitions(definitionIds) {
  return (dispatch, getState, httpClient) => {
    dispatch(definitionsRequested(definitionIds));
    return definitionsClient.fetchDefinitionsList(definitionIds, httpClient).then(data => {
      const {
        results
      } = data;
      dispatch(definitionsRequestSucceeded(definitionIds, results));
      return results;
    }).catch(error => {
      reportErrorToRaven(error, 'Error fetching definitions');
      dispatch(definitionsRequestFailed(definitionIds, error));
    });
  };
}
const deleteDefinitionSmartRulesRequested = meta => ({
  type: DELETE_SMART_DEFINITION_CRITERIA,
  status: RequestStatus.PENDING,
  meta: Object.assign({}, meta)
});
const deleteDefinitionSmartRulesRequestSucceeded = (meta, payload, definition) => {
  // Prevent race condition where deletion may cause
  // these definition properties to be undefined
  const trackingData = {
    targetType: definition.targetType,
    moduleName: definition.moduleName,
    smartType: definition.name
  };
  return {
    type: DELETE_SMART_DEFINITION_CRITERIA,
    payload,
    status: RequestStatus.SUCCEEDED,
    meta: Object.assign({}, meta, {
      [TRACK_EVENT]: {
        action: 'delete-definition',
        usageTrackerJsName: 'smartContentDefinitionInteraction',
        meta: Object.assign({
          name: 'delete-definition',
          type: TrackEventTypes.INTERACTION
        }, trackingData)
      }
    })
  };
};
const deleteDefinitionSmartRulesRequestFailed = (meta, error) => ({
  type: DELETE_SMART_DEFINITION_CRITERIA,
  payload: error,
  error: true,
  status: RequestStatus.FAILED,
  meta: Object.assign({}, meta)
});
export function deleteDefinitionCriteria(definitionId, criteria) {
  return (dispatch, getState, httpClient) => {
    const state = getState();
    const definition = getDefinition(state, {
      definitionId
    }) || {};
    if (!criteria) {
      criteria = definition.criteria;
    }
    if (!criteria || !criteria.length) {
      return Promise.reject(new Error(`No criteria to delete for definition ${definitionId}`));
    }
    const meta = {
      definitionId,
      criteria
    };
    dispatch(deleteDefinitionSmartRulesRequested(meta));
    return criteriaClient.bulkDeleteCriteria(criteria, httpClient).then(data => {
      dispatch(deleteDefinitionSmartRulesRequestSucceeded(meta, data, definition));
      return data;
    }).catch(error => {
      reportErrorToRaven(error, 'Error deleting criteria');
      dispatch(deleteDefinitionSmartRulesRequestFailed(meta, error));
      return error;
    });
  };
}
const resetDefinition = definitionId => ({
  type: RESET_DEFINITIONS_FETCH_STATUS,
  payload: {
    definitionId
  }
});
export const resetDefinitionsFetchStatus = definitionId => {
  return dispatch => {
    dispatch(resetDefinition(definitionId));
  };
};